import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Network } from "lucide-react";
import { LanguageToggle } from "@/components/LanguageToggle";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PublicShellProps {
  children: React.ReactNode;
  /** When true the header overlays a dark hero (landing page). */
  transparentHeader?: boolean;
}

const NAV_LINKS = [
  { to: "/pricing", labelKey: "public.nav.pricing" },
  { to: "/privacy", labelKey: "public.footer.privacy" },
  { to: "/terms", labelKey: "public.footer.terms" },
] as const;

export default function PublicShell({
  children,
  transparentHeader = false,
}: PublicShellProps) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  return (
    <>
      <motion.header
        initial={reduced ? false : { opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
        className={cn(
          "sticky top-0 z-50 w-full backdrop-blur-md",
          transparentHeader
            ? "dark border-b border-border/40 bg-background/60 text-foreground"
            : "border-b border-border/60 bg-background/80",
        )}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-2.5 transition-opacity hover:opacity-80"
          >
            <span className="glow-primary flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Network className="size-4" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-foreground">
              {t("brand")}
            </span>
          </Link>
          <nav className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  buttonVariants({ variant: "ghost", size: "sm" }),
                  link.to !== "/pricing" && "hidden sm:inline-flex",
                )}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <LanguageToggle />
            <Link
              to="/login"
              className={cn(
                buttonVariants({ size: "sm" }),
                "rounded-full px-4 transition-shadow hover:glow-primary",
              )}
            >
              {t("auth.signIn")}
            </Link>
          </nav>
        </div>
      </motion.header>

      {children}

      <footer className="border-t border-border/60 bg-surface-container/30 px-4 py-6 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <span className="flex size-5 items-center justify-center rounded bg-primary text-primary-foreground">
              <Network className="size-2.5" aria-hidden="true" />
            </span>
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t("public.footer.copyright")}
            </p>
          </div>
          <nav className="flex shrink-0 gap-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </>
  );
}
